<script setup lang="ts">
import { useSnackbarStore } from '@/store/snackbar'
import {
  $getChangeRequestSubtask,
  $getChangeRequestSubtaskOptions,
  $updateChangeRequestSubtask
} from '@/api/changeRequests'
import type { ChangeRequestSubtask, SubtaskStatusOption, SubtaskTypeOption, UserSelect } from '@/models/changeRequest'
import { uniqBy } from 'lodash'

interface IProps {
  modelValue?: boolean,
  changeRequestSubtaskId?: number | string | undefined,
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: false,
  changeRequestSubtaskId: undefined,
})

interface Emit {
  (e: 'update:modelValue', value: boolean): void
  (e: 'onClose'): void
  (e: 'onUpdateSubtask'): void
}

const emit = defineEmits<Emit>()

const route = useRoute()

const snackbarStore = useSnackbarStore()

const showSelectUsersDialog = ref<boolean>(false)

const changeRequestSubtask = ref<ChangeRequestSubtask | null>(null)

const subtaskStatusOptions = ref<SubtaskStatusOption[]>([])
const subtaskTypeOptions = ref<SubtaskTypeOption[]>([])

const editSubtaskId = ref<number | null>(null)

const loading = ref<boolean>(false)

const getChangeRequestSubtask = async () => {
  if (!props.changeRequestSubtaskId) return

  loading.value = true

  try {
    const res = await $getChangeRequestSubtask(props.changeRequestSubtaskId)
    changeRequestSubtask.value = res.data
  } catch (e: any) {
    console.log(e)
  } finally {
    loading.value = false
  }
}

const getChangeRequestSubtaskOptions = async () => {
  try {
    const res = await $getChangeRequestSubtaskOptions()
    subtaskStatusOptions.value = res.data.actions.POST.status.choices
    subtaskTypeOptions.value = res.data.actions.POST.subtask_type.choices
  } catch (e: any) {
    console.log(e)
  }
}

const updateChangeRequestSubtask = async () => {
  if (!changeRequestSubtask.value || !changeRequestSubtask.value.id) return

  loading.value = true

  const payload = {
    description: changeRequestSubtask.value.description ? changeRequestSubtask.value.description : '',
    subtask_type: changeRequestSubtask.value.subtask_type ? changeRequestSubtask.value.subtask_type : undefined,
    status: changeRequestSubtask.value.status ? changeRequestSubtask.value.status : undefined,
    assigned_to_users:
      changeRequestSubtask.value?.assigned_to_users_info &&
      Array.isArray(changeRequestSubtask.value.assigned_to_users_info)
        ? changeRequestSubtask.value.assigned_to_users_info.map(i => i.id)
        : [],
    due_date: changeRequestSubtask.value.due_date ? changeRequestSubtask.value.due_date : null,
  } as ChangeRequestSubtask

  try {
    await $updateChangeRequestSubtask(String(changeRequestSubtask.value.id), payload)

    changeRequestSubtask.value = null
    editSubtaskId.value = null

    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Subtask updated successfully!'
    })
    await getChangeRequestSubtask()

    emit('onUpdateSubtask')
  } catch (e: any) {
    console.log(e)
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: 'Oops, something went wrong, try again'
    })
  } finally {
    loading.value = false
  }
}

const onRequestSubtaskSelectUsers = (users: UserSelect[]) => {
  if (!changeRequestSubtask.value) return
  const currentAssignedToUsers = changeRequestSubtask.value?.assigned_to_users_info?.length ?
    changeRequestSubtask.value.assigned_to_users_info : []
  // Set uniq users
  changeRequestSubtask.value.assigned_to_users_info = uniqBy([ ...currentAssignedToUsers, ...users], 'id')
}

const hideDialog = () => {
  emit('update:modelValue', false)
  onClose()
}

const onClose = async () => {
  editSubtaskId.value = null
  emit('onClose')
}

watch(() => props.modelValue, () => {
  // on show Dialog
  if (!!props.modelValue) {
    getChangeRequestSubtask()
    getChangeRequestSubtaskOptions()
  }
  // on hide Dialog
  else {
    onClose()
  }
})
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="800"
    :persistent="!!loading"
    @after-leave="onClose"
    @update:modelValue="emit('update:modelValue')"
  >
    <VBtn
      variant="elevated"
      density="compact"
      icon="tabler-x"
      :disabled="!!loading"
      class="v-dialog-close-btn"
      @click="hideDialog"
    />

    <VCard>
      <VCardText>
        <VOverlay
          v-model="loading"
          persistent
          absolute
          contained
          class="align-center justify-center"
        >
          <VProgressCircular
            indeterminate
            color="primary"
          />
        </VOverlay>

        <SelectUsersDialog
          v-model="showSelectUsersDialog"
          :dialog-title="'Assigned To Users'"
          :user-select-label="'Assigned To'"
          @on-submit="users => onRequestSubtaskSelectUsers(users)"
        />

        <ChangeRequestSubtaskInfo
          v-model:show-select-users-dialog="showSelectUsersDialog"
          :change-request-loading="loading"
          :subtask-status-options="subtaskStatusOptions"
          :subtask-type-options="subtaskTypeOptions"
          :request-subtask="changeRequestSubtask"
          :edit-subtask-id="editSubtaskId"
          :hide-delete-btn="true"
          :hide-duplicate-btn="true"
          @update:edit-subtask-id="(val) => { editSubtaskId = val }"
          @update:request-subtask="(val) => !!editSubtaskId && val ? changeRequestSubtask = val : ''"
          @onUpdateChangeRequestSubtask="updateChangeRequestSubtask()"
          @onAddChangeRequestSubtaskComment="getChangeRequestSubtask()"
        />
      </VCardText>
    </VCard>
  </VDialog>
</template>
